// Finans (C53–C57, P61–P62; FINANCE bayrağı): şoför hakedişi, müşteri faturalama ve fatura taslağı, ek km / ek sefer,
// okul servis ödemeleri (veli). Zod girdileri + yanıt tipleri; API ve istemciler aynı tipi kullanır. Para her yerde kuruş (Int).
// Kurum tarafı fatura okuma tipleri `./commercial` (InvoiceRow, InvoiceDetail, ExtraChargeItem) — burada yeniden kullanılır.
import { z } from 'zod';
import type { Direction } from '../enums';
import type { Paged } from './common';
import type { ContractPricingModel } from './business';
import {
  EXTRA_CHARGE_STATUSES,
  INVOICE_FILTERS,
  INVOICE_LINE_TYPES,
  type CommercialParty,
  type ExtraChargeItem,
  type InvoiceDetail,
  type InvoiceLineType,
  type InvoiceRow,
  type InvoiceStatus,
  type InvoiceSummary,
} from './commercial';

/** Dönem: takvim ayı (İstanbul) "2026-10". */
export const Period = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Dönem YYYY-AA biçiminde olmalı');
const DateOnly = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Tarih YYYY-AA-GG biçiminde olmalı');
const Ref = z.string().min(1).max(64);

const PageFields = {
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(50),
};

export const FinanceIdsInput = z.object({
  ids: z.array(Ref).min(1, 'En az bir kayıt seçin.').max(200, 'Tek seferde en fazla 200 kayıt.'),
});
export type FinanceIdsInput = z.infer<typeof FinanceIdsInput>;

export interface FinanceBulkResult {
  updated: number;
  /** durumu uygun olmadığı için atlanan kayıtlar */
  skipped: number;
}

// ───────── şoför ödemeleri (C53/C54) ─────────

export const DRIVER_PAYMENT_STATUSES = ['DRAFT', 'APPROVED', 'PAID'] as const;
export type DriverPaymentStatus = (typeof DRIVER_PAYMENT_STATUSES)[number];
export const DRIVER_PAYMENT_STATUS_LABELS: Record<DriverPaymentStatus, string> = {
  DRAFT: 'Taslak',
  APPROVED: 'Onaylandı',
  PAID: 'Ödendi',
};

/** Hakediş oranları (kuruş). Formül: aylık sabit + tamamlanan sefer × sefer ücreti + km × km ücreti. */
export const DriverPayRatesSchema = z.object({
  tripRate: z.number().int('Kuruş tam sayı olmalı').min(0).max(10_000_000),
  kmRate: z.number().int('Kuruş tam sayı olmalı').min(0).max(1_000_000),
  fixed: z.number().int('Kuruş tam sayı olmalı').min(0).max(100_000_000),
});
export type DriverPayRates = z.infer<typeof DriverPayRatesSchema>;
/** İlk hesaplamada önerilen oranlar (firma daha önce hesaplamadıysa). */
export const DEFAULT_DRIVER_PAY_RATES: DriverPayRates = { tripRate: 25_000, kmRate: 400, fixed: 2_000_000 };

/** Tek seferin hakediş tutarı (sabit hariç). */
export function driverTripAmount(r: Pick<DriverPayRates, 'tripRate' | 'kmRate'>, km: number): number {
  return r.tripRate + Math.round(km * r.kmRate);
}

/** Dönem taban tutarı: sabit + sefer × sefer ücreti + km × km ücreti (km tam sayıya yuvarlanır). */
export function driverPayBase(r: DriverPayRates, trips: number, km: number): number {
  return r.fixed + trips * r.tripRate + Math.round(km) * r.kmRate;
}

export const DriverPaymentQuery = z.object({
  period: Period.optional(),
  status: z.enum([...DRIVER_PAYMENT_STATUSES, 'all']).default('all'),
  q: z.string().trim().max(60).optional(),
  ...PageFields,
});
export type DriverPaymentQuery = z.input<typeof DriverPaymentQuery>;

export const DriverPaymentExportQuery = z.object({
  period: Period,
  status: z.enum([...DRIVER_PAYMENT_STATUSES, 'all']).default('all'),
  format: z.enum(['xlsx', 'pdf']).default('xlsx'),
});

export interface DriverPaymentRow {
  id: string;
  period: string;
  driver: { id: string; name: string; employeeNo: string | null };
  tripCount: number;
  km: number;
  baseAmount: number;
  bonus: number;
  deduction: number;
  total: number;
  status: DriverPaymentStatus;
  paidAt: string | null;
}

export interface DriverPaymentTotals {
  count: number;
  tripCount: number;
  km: number;
  base: number;
  bonus: number;
  deduction: number;
  total: number;
  byStatus: Record<DriverPaymentStatus, { count: number; total: number }>;
}

export interface DriverPaymentList extends Paged<DriverPaymentRow> {
  period: string;
  /** seçilebilir dönemler (yeniden eskiye) */
  periods: string[];
  /** dönemin tamamı (süzgeçten bağımsız) */
  totals: DriverPaymentTotals;
  /** "Dönemi hesapla" için önerilen oranlar: son kullanılan ya da varsayılan */
  rates: DriverPayRates;
  ratesSource: 'LAST_USED' | 'DEFAULT';
  /** dönemde tamamlanmış seferi olup hakediş kaydı olmayan şoför sayısı */
  missingDrivers: number;
  lastComputedAt: string | null;
}

export const DriverPaymentRecomputeInput = z.object({ period: Period, rates: DriverPayRatesSchema });
export type DriverPaymentRecomputeInput = z.infer<typeof DriverPaymentRecomputeInput>;

export interface DriverPaymentRecomputeResult {
  period: string;
  created: number;
  updated: number;
  /** onaylanmış/ödenmiş olduğu için dokunulmayan kayıtlar */
  skipped: number;
}

export const ADJUSTMENT_KINDS = ['BONUS', 'DEDUCTION'] as const;
export type AdjustmentKind = (typeof ADJUSTMENT_KINDS)[number];
export const ADJUSTMENT_KIND_LABELS: Record<AdjustmentKind, string> = { BONUS: 'Prim', DEDUCTION: 'Kesinti' };

export const DriverPaymentAdjustmentInput = z.object({
  kind: z.enum(ADJUSTMENT_KINDS),
  amount: z.number().int().min(100, 'Tutar en az 1 ₺ olmalı.').max(100_000_000, 'Tutar çok yüksek.'),
  reason: z.string().trim().min(3, 'Gerekçe yazın (en az 3 karakter).').max(140),
});
export type DriverPaymentAdjustmentInput = z.infer<typeof DriverPaymentAdjustmentInput>;

export interface DriverPaymentAdjustment {
  id: string;
  kind: AdjustmentKind;
  /** pozitif tutar (kuruş) */
  amount: number;
  reason: string;
  at: string | null;
  by: string | null;
}

export interface DriverPaymentLine {
  label: string;
  /** kesinti negatif */
  amount: number;
  kind: 'BASE' | AdjustmentKind;
}

export interface DriverPaymentTripLine {
  id: string;
  date: string;
  direction: Direction;
  routeCode: string;
  routeName: string;
  km: number;
  amount: number;
}

export type DriverPaymentEventKind = 'COMPUTED' | 'ADJUSTED' | 'ADJUSTMENT_REMOVED' | 'APPROVED' | 'PAID' | 'REOPENED';
export interface DriverPaymentEvent {
  at: string;
  kind: DriverPaymentEventKind;
  label: string;
  by: string | null;
  note: string | null;
}

export interface DriverPaymentDetail extends DriverPaymentRow {
  /** kaydın hesaplandığı oranlar (eski kayıtlarda yok) */
  rates: DriverPayRates | null;
  lines: DriverPaymentLine[];
  adjustments: DriverPaymentAdjustment[];
  /** dönemde tamamlanan seferler (sefer başı tutar oranlarla) */
  trips: DriverPaymentTripLine[];
  events: DriverPaymentEvent[];
  computedAt: string | null;
  /** kayıt hesaplandıktan sonra sefer sayısı/km değişti mi (taslakta yeniden hesapla önerisi) */
  stale: boolean;
}

// ───────── müşteri faturalama (C55) ─────────

export const DEFAULT_VAT_RATE = 20;
export const VAT_RATES = [0, 1, 10, 20] as const;
/** Kesimde son ödeme tarihi verilmezse: kesim + 15 gün. */
export const DEFAULT_PAYMENT_TERM_DAYS = 15;

export const BillingQuery = z.object({ period: Period.optional() });

export interface BillingBasis {
  quantity: number;
  /** "araç", "km", "yolcu", "sefer" */
  unit: string;
  /** "9 araç · aylık servis bedeli" */
  description: string;
}

export interface BillingRow {
  contract: {
    id: string;
    number: string;
    status: string;
    pricingModel: ContractPricingModel;
    unitPrice: number;
    extraKmPrice: number;
    extraTripPrice: number;
  };
  institution: CommercialParty & { kind: string | null };
  basis: BillingBasis;
  baseAmount: number;
  /** onaylanmış, henüz faturalanmamış ek ücretler (sonraki taslağa eklenir) */
  extras: { count: number; amount: number };
  /** beklenen tutar: taban + onaylı ek ücretler; KDV varsayılan oranla */
  subtotal: number;
  vat: number;
  total: number;
  invoice: { id: string; number: string; status: InvoiceStatus; total: number; daysOverdue: number | null } | null;
}

export interface BillingOverview {
  period: string;
  periods: string[];
  vatRate: number;
  rows: BillingRow[];
  totals: {
    /** KDV dahil beklenen */
    expected: number;
    /** kesilmiş (ödenmiş dahil) fatura toplamı */
    invoiced: number;
    paid: number;
    draftCount: number;
    missingCount: number;
  };
}

export const FinanceInvoiceQuery = z.object({
  status: z.enum(INVOICE_FILTERS).default('all'),
  institutionId: Ref.optional(),
  period: Period.optional(),
  q: z.string().trim().max(40).optional(),
  ...PageFields,
});
export type FinanceInvoiceQuery = z.input<typeof FinanceInvoiceQuery>;

export interface FinanceInvoiceList extends Paged<InvoiceRow> {
  summary: InvoiceSummary & { draft: number; draftCount: number };
  institutions: CommercialParty[];
}

export const InvoiceDraftInput = z.object({ contractId: Ref, period: Period });
export type InvoiceDraftInput = z.infer<typeof InvoiceDraftInput>;

export const InvoiceLineInput = z.object({
  /** var olan kalem; yoksa yeni kalem */
  id: Ref.optional(),
  type: z.enum(INVOICE_LINE_TYPES),
  description: z.string().trim().min(2, 'Kalem açıklaması yazın.').max(160),
  quantity: z.number().min(0, 'Miktar negatif olamaz.').max(1_000_000),
  unitPrice: z.number().int('Birim fiyat kuruş cinsinden tam sayı olmalı.').min(0).max(1_000_000_000),
});
export type InvoiceLineInput = z.infer<typeof InvoiceLineInput>;

export const InvoiceDraftUpdateInput = z.object({
  lines: z.array(InvoiceLineInput).min(1, 'En az bir kalem ekleyin.').max(100),
  vatRate: z.number().int().min(0).max(30).default(DEFAULT_VAT_RATE),
});
export type InvoiceDraftUpdateInput = z.input<typeof InvoiceDraftUpdateInput>;

export const InvoiceIssueInput = z.object({ dueAt: DateOnly.optional() });
export type InvoiceIssueInput = z.infer<typeof InvoiceIssueInput>;
export const InvoicePaidInput = z.object({ paidAt: DateOnly.optional() });
export type InvoicePaidInput = z.infer<typeof InvoicePaidInput>;

/** Kalem tutarı: miktar × birim fiyat (kuruşa yuvarlanır); indirim negatif. */
export function invoiceLineAmount(type: InvoiceLineType, quantity: number, unitPrice: number): number {
  const a = Math.round(quantity * unitPrice);
  return type === 'DISCOUNT' ? -a : a;
}

export function invoiceTotals(
  lines: { amount: number }[],
  vatRate: number,
): { subtotal: number; vat: number; total: number } {
  const subtotal = lines.reduce((s, l) => s + l.amount, 0);
  const vat = Math.round((subtotal * vatRate) / 100);
  return { subtotal, vat, total: subtotal + vat };
}

export type InvoiceEventKind = 'CREATED' | 'UPDATED' | 'EXTRAS_ADDED' | 'ISSUED' | 'PAID' | 'DOWNLOADED';
export interface InvoiceEvent {
  at: string;
  kind: InvoiceEventKind;
  label: string;
  by: string | null;
}

/** C56: fatura detayı + firma tarafı bilgileri (akış, ek ücret bağlantıları, düzenlenebilirlik). */
export interface FinanceInvoiceDetail {
  invoice: InvoiceDetail;
  editable: boolean;
  /** kalem kimliği → ek ücret kaydı (taslağa ek ücretten gelen kalemler) */
  chargeLinks: Record<string, string>;
  /** kurumun onaylı, henüz faturalanmamış ek ücretleri (taslağa eklenebilir) */
  pendingExtras: ExtraChargeItem[];
  events: InvoiceEvent[];
  /** kesimde önerilen son ödeme tarihi (YYYY-AA-GG) */
  suggestedDueAt: string;
}

export interface InvoiceMutationResult {
  id: string;
  number: string;
  status: InvoiceStatus;
}

// ───────── ek km / ek sefer (C57) ─────────

export const EXTRA_CHARGE_TYPES = ['EXTRA_KM', 'EXTRA_TRIP'] as const;
export type ExtraChargeType = (typeof EXTRA_CHARGE_TYPES)[number];

export const FinanceExtraQuery = z.object({
  status: z.enum(['open', ...EXTRA_CHARGE_STATUSES, 'all']).default('open'),
  institutionId: Ref.optional(),
  type: z.enum(EXTRA_CHARGE_TYPES).optional(),
  ...PageFields,
});
export type FinanceExtraQuery = z.input<typeof FinanceExtraQuery>;

export interface FinanceInstitutionOption {
  id: string;
  name: string;
  contractId: string;
  contractNumber: string;
  extraKmPrice: number;
  extraTripPrice: number;
}

export interface FinanceExtraList extends Paged<ExtraChargeItem> {
  counts: { pending: number; approved: number };
  totals: { pending: number; approved: number };
  institutions: FinanceInstitutionOption[];
}

export const ExtraChargeCreateInput = z
  .object({
    institutionId: Ref,
    type: z.enum(EXTRA_CHARGE_TYPES),
    quantity: z.number().positive('Miktar sıfırdan büyük olmalı.').max(100_000),
    /** verilmezse sözleşmedeki ek km / ek sefer fiyatı */
    unitPrice: z.number().int().min(0).max(100_000_000).optional(),
    tripId: Ref.optional(),
    note: z.string().trim().min(3, 'Gerekçe yazın (en az 3 karakter).').max(200),
  })
  .refine((v) => v.type !== 'EXTRA_TRIP' || Number.isInteger(v.quantity), {
    message: 'Ek sefer sayısı tam sayı olmalı.',
    path: ['quantity'],
  });
export type ExtraChargeCreateInput = z.input<typeof ExtraChargeCreateInput>;

export const ExtraChargeDecisionInput = z.discriminatedUnion('decision', [
  z.object({ decision: z.literal('APPROVE'), ids: FinanceIdsInput.shape.ids }),
  z.object({
    decision: z.literal('REJECT'),
    ids: FinanceIdsInput.shape.ids,
    reason: z.string().trim().min(3, 'Ret gerekçesi yazın (en az 3 karakter).').max(200),
  }),
]);
export type ExtraChargeDecisionInput = z.infer<typeof ExtraChargeDecisionInput>;

export const ExtraTripQuery = z.object({ institutionId: Ref, date: DateOnly });
export interface ExtraTripOption {
  id: string;
  date: string;
  direction: Direction;
  routeCode: string;
  routeName: string;
  plate: string;
  km: number | null;
  status: string;
}

// ───────── veli ödemeleri (C55 sekmesi, P61/P62) ─────────

export const PARENT_PAYMENT_STATUSES = ['PAID', 'UPCOMING', 'OVERDUE'] as const;
export type ParentPaymentStatus = (typeof PARENT_PAYMENT_STATUSES)[number];
/** Görünen durum: yakın vadeli (≤ 30 gün) "Yaklaşıyor", daha ilerisi "Planlandı". */
export type ParentPaymentDisplay = 'PAID' | 'DUE_SOON' | 'SCHEDULED' | 'OVERDUE';
export const PARENT_PAYMENT_DISPLAY_LABELS: Record<ParentPaymentDisplay, string> = {
  PAID: 'Ödendi',
  DUE_SOON: 'Yaklaşıyor',
  SCHEDULED: 'Planlandı',
  OVERDUE: 'Gecikti',
};
export const PAYMENT_DUE_SOON_DAYS = 30;
/** Hatırlatma e-postası (E05 YAKLASIYOR) vadeden bu kadar gün önce. */
export const PAYMENT_REMINDER_DAYS = 3;
/** Uygulama içi ödeme yok (sağlayıcı kararı açık): veliye dürüst not. */
export const PAYMENT_METHOD_NOTE = 'Ödeme yöntemi okulunuzca bildirilecek.';

export function parentPaymentDisplay(
  status: string,
  dueAt: string | Date,
  now: number = Date.now(),
): ParentPaymentDisplay {
  if (status === 'PAID') return 'PAID';
  const due = new Date(dueAt).getTime();
  if (status === 'OVERDUE' || due < now) return 'OVERDUE';
  return due - now <= PAYMENT_DUE_SOON_DAYS * 86_400_000 ? 'DUE_SOON' : 'SCHEDULED';
}

export const ParentPaymentQuery = z.object({
  status: z.enum(['open', 'OVERDUE', 'DUE_SOON', 'PAID', 'all']).default('open'),
  institutionId: Ref.optional(),
  period: Period.optional(),
  q: z.string().trim().max(60).optional(),
  ...PageFields,
});
export type ParentPaymentQuery = z.input<typeof ParentPaymentQuery>;

export interface ParentPaymentRow {
  id: string;
  period: string;
  installment: number;
  installments: number;
  amount: number;
  dueAt: string;
  paidAt: string | null;
  status: ParentPaymentDisplay;
  receiptNo: string | null;
  child: { id: string; name: string; className: string | null };
  /** KVKK: kısaltılmış ad ("Emrah U."); e-posta adresi gösterilmez */
  guardian: { name: string; hasEmail: boolean };
  institution: CommercialParty;
}

export interface ParentPaymentList extends Paged<ParentPaymentRow> {
  summary: {
    overdue: number;
    overdueCount: number;
    dueSoon: number;
    dueSoonCount: number;
    /** bu ay tahsil edilen */
    collectedThisMonth: number;
  };
  institutions: CommercialParty[];
}

export const ParentPaymentPaidInput = z.object({
  /** makbuz / fatura no; boşsa otomatik "MKB-2026-00001" */
  receiptNo: z.string().trim().max(40).optional(),
  paidAt: DateOnly.optional(),
});
export type ParentPaymentPaidInput = z.infer<typeof ParentPaymentPaidInput>;

export interface ParentPaymentPaidResult {
  id: string;
  receiptNo: string;
  /** E05 "ödemeniz alındı" e-postası gönderildi mi (velinin e-postası yoksa gönderilmez) */
  emailed: boolean;
}

/** P61/P62 veli görünümü. */
export interface GuardianPaymentItem {
  id: string;
  childId: string;
  childName: string;
  period: string;
  installment: number;
  installments: number;
  amount: number;
  dueAt: string;
  paidAt: string | null;
  status: ParentPaymentDisplay;
  receiptNo: string | null;
  /** vadeye kalan gün (geçtiyse negatif); ödendiyse null */
  daysLeft: number | null;
}

export interface GuardianPaymentChild {
  id: string;
  firstName: string;
  lastName: string;
  initials: string;
  className: string | null;
  institution: string;
  items: GuardianPaymentItem[];
  paidCount: number;
  paidAmount: number;
  totalAmount: number;
  overdueCount: number;
}

export interface GuardianPayments {
  next: GuardianPaymentItem | null;
  overdue: { count: number; amount: number };
  children: GuardianPaymentChild[];
  note: string;
}

export interface GuardianPaymentDetail extends GuardianPaymentItem {
  className: string | null;
  institution: string;
  provider: string;
  howToPay: string[];
}
